// Story 11.12 (ADO-1963), T2 (AC1, AC4): one product's WOW quote, read on its own.
//
// The shared price store (`usePriceStore`) holds one entry and account at a time, and changing
// its key clears its entries and supersedes its pending reads, so loading the WOW entry into it
// would wipe the page's own prices. This hook never touches it: it reads the WOW quote for one
// SKU into the component's own state (per PDP, not global; ADR-004 keeps global state in
// Zustand, and nothing else needs this answer).
//
// The WOW context (`wowOfferContextOf`) is the session's ordering choices: the entry of kind
// `WOW` and its account by `resolveChoice` with none named (`null`, an entry offering several,
// is never guessed). There is no context, so no request and no offer, when:
//   - real prices are off (`VITE_REAL_PRICES_ENABLED`): there is no real price to compare;
//   - no entry is active, or the active entry is the WOW entry itself;
//   - the choices offer no WOW entry, or it resolves to no single account. A staff entry offers
//     no account (staff pick a store account for the active entry only), so staff never have one.
//
// The hook also makes the comparison (`compareWithWow`) against the active quote the caller
// passes (the PDP's own, from the shared price store), so a caller and Story 11.5's badge get
// the same answer.
//
// Freshness (the story's "Freshness" section): a read on every context change; again when the
// tab becomes visible and on window focus; every 60 seconds while the document is visible and
// the offer is shown (the comparison holds) or its last read went unanswered; and whenever the caller asks (`reread`, after a
// refused or uncertain add). Each read has its own AbortController. A context change, a caller's
// `reread` and unmounting abort the one in flight (a context change or a refused add makes it
// stale); a return to the page and the minute's poll do not, and read nothing while a read for
// the same context is on its way (returning to a tab fires both `visibilitychange` and `focus`,
// and that is one read). An answer is kept only for the context it was read for, so a late answer
// for another SKU, account or session is never shown.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CartTarget } from '../api/cart-target';
import { fetchPriceQuotes, type PriceQuoteDto } from '../api/price-quotes.client';
import type { OrderingEntry } from '../api/ordering-choices.client';
import { useSessionStore, type SessionContext } from '../auth/useSessionStore';
import { cartTargetOf, resolveChoice } from '../ordering/orderingChoices';
import { compareWithWow, type ComparableQuote, type WowComparison } from './cheaperOnWow';
import { realPricesEnabled } from './realPrices';

/** How often a shown offer is read again while the page is visible. */
export const WOW_OFFER_REFRESH_MS = 60_000;

/** Where the WOW offer is read and acted on: the WOW entry, its one account, the WOW cart. */
export interface WowOfferContext {
  readonly entry: OrderingEntry;
  readonly customerAccountCode: string;
  /** The WOW cart (`cartTargetOf`), never the session's cart. */
  readonly cart: CartTarget;
  /** The signed-in session the context belongs to (tenant and subject). */
  readonly sessionKey: string;
}

type OfferSession = Pick<
  SessionContext,
  'orderingChoices' | 'activeEntryKey' | 'identity' | 'tenantId'
>;

/** The WOW context for `session`, or `null` when the store is offered no WOW route (see the
 *  header). Pure: `realPrices` is passed in. */
export function wowOfferContextOf(
  session: OfferSession,
  realPrices: boolean,
): WowOfferContext | null {
  const { orderingChoices: choices, activeEntryKey } = session;
  if (!realPrices || choices === null || activeEntryKey === null) return null;
  const wowEntry = choices.entries.find((entry) => entry.kind === 'WOW');
  if (wowEntry === undefined || wowEntry.key === activeEntryKey) return null;
  const resolved = resolveChoice(choices, wowEntry.key, null);
  const customerAccountCode = resolved?.account?.customerAccountCode ?? null;
  if (resolved === null || customerAccountCode === null) return null;
  return {
    entry: resolved.entry,
    customerAccountCode,
    cart: cartTargetOf(resolved.entry, customerAccountCode),
    sessionKey: `${session.tenantId ?? ''}|${session.identity?.sub ?? ''}`,
  };
}

/** One string for "this SKU, at this WOW entry and account, for this session". */
function contextKeyOf(sku: string, context: WowOfferContext): string {
  return [sku, context.entry.key, context.customerAccountCode, context.sessionKey].join('\n');
}

/** The session's WOW context, as a subscribed value: a new object only when it changes. */
export function useWowOfferContext(): WowOfferContext | null {
  const orderingChoices = useSessionStore((s) => s.orderingChoices);
  const activeEntryKey = useSessionStore((s) => s.activeEntryKey);
  const identity = useSessionStore((s) => s.identity);
  const tenantId = useSessionStore((s) => s.tenantId);
  const realPrices = realPricesEnabled();
  return useMemo(
    () => wowOfferContextOf({ orderingChoices, activeEntryKey, identity, tenantId }, realPrices),
    [orderingChoices, activeEntryKey, identity, tenantId, realPrices],
  );
}

export interface WowOfferRead {
  /** The WOW context, or `null` when there is none (and nothing is read). */
  readonly context: WowOfferContext | null;
  /** The product's WOW quote for `context`, or `undefined` when not read yet, unread, or read
   *  for another context. A 403 on the entry is an unpriced quote (`ENTRY_FORBIDDEN`). */
  readonly quote: PriceQuoteDto | undefined;
  /** The active quote against `quote` (`compareWithWow`): the offer is shown only when cheaper. */
  readonly comparison: WowComparison;
  /** Read the quote again now (after a refused or uncertain add). A no-op with no context. */
  readonly reread: () => void;
}

interface Answer {
  readonly key: string;
  readonly quote: PriceQuoteDto | undefined;
  /** Nobody answered (a failed or aborted request, an unusable entry): not the same as an
   *  answer of "unpriced", so polling goes on until one comes. */
  readonly unread: boolean;
}

/**
 * The WOW quote for `sku` at the session's WOW context, read on its own, compared with `active`
 * (the product's quote at the session's active entry; `undefined` while it has none). While it
 * is cheaper the quote is read again every {@link WOW_OFFER_REFRESH_MS} the page is visible.
 */
export function useWowOffer(sku: string, active: ComparableQuote | undefined): WowOfferRead {
  const context = useWowOfferContext();
  const contextKey = context === null ? null : contextKeyOf(sku, context);
  // The read depends on these values, not on the context object: a background refresh of the
  // ordering choices rebuilds the object without changing where the offer is read.
  const entryKey = context?.entry.key ?? null;
  const accountCode = context?.customerAccountCode ?? null;
  const [answer, setAnswer] = useState<Answer | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const readCount = useRef(0);

  /** Read the quote. `unlessReading`: leave a read already on its way (always one for the current
   *  context: a context change aborts the old one) to answer instead of starting another. */
  const read = useCallback(
    (unlessReading: boolean) => {
      if (unlessReading && inFlight.current !== null) return;
      inFlight.current?.abort();
      inFlight.current = null;
      if (contextKey === null || entryKey === null || accountCode === null) return;
      const controller = new AbortController();
      inFlight.current = controller;
      const readNumber = ++readCount.current;
      void fetchPriceQuotes(entryKey, accountCode, [sku], controller.signal)
        .then(({ quotes }) => {
          // Only the latest read, for the context it was made for, is kept.
          if (controller.signal.aborted || readNumber !== readCount.current) return;
          const quote = quotes.find((each) => each.skuCode === sku);
          setAnswer({ key: contextKey, quote, unread: quote === undefined });
        })
        .finally(() => {
          // However it ended, it is no longer on its way (a newer read has replaced it otherwise).
          if (inFlight.current === controller) inFlight.current = null;
        });
    },
    [contextKey, entryKey, accountCode, sku],
  );
  /** A read now, whatever is on its way (after a refused or uncertain add). */
  const reread = useCallback(() => read(false), [read]);

  // Every context change reads (and aborts what the old context had in flight); unmount aborts.
  useEffect(() => {
    read(false);
    return () => {
      inFlight.current?.abort();
      inFlight.current = null;
      readCount.current += 1;
    };
  }, [read]);

  // Back to the tab or the window: read again at once, once (both events fire on a return).
  useEffect(() => {
    if (contextKey === null) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') read(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [contextKey, read]);

  const current = answer !== null && answer.key === contextKey ? answer : null;
  const quote = current?.quote;
  const comparison = compareWithWow(active, quote);
  // An unread answer hides the offer (never shown unconfirmed) but keeps polling, so one
  // transient failure does not remove it until the next focus; an answer (unpriced, 403) stops.
  const poll = comparison.cheaper || current?.unread === true;

  // While the offer is shown (or its last read went unanswered) and the page is visible, read
  // again every minute.
  useEffect(() => {
    if (!poll || contextKey === null) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') read(true);
    }, WOW_OFFER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [poll, contextKey, read]);

  return { context, quote, comparison, reread };
}
